import { evaluateCondition } from '@dynamic/ui';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Condition } from '#/client';
import { MoreItem } from '#/service/config';
import { NavigationType } from '#/service/constants';
import { destinationRoutes, Path } from '#/service/routes';
import { useAuthentication } from '#/store/auth';

import { useConfig, useDataContext } from './';
import { useNavigation } from './useNavigation';

/**
 * A guard that validates if the user can access the current page by validating
 * conditional routes. It only takes settings conditions into account, and ignores
 * menu item conditions. If the condition evaluation returns false then
 * redirects to the home page. Registration route is excluded from the guard.
 */
export function usePageGuard() {
  const { pathname } = useLocation();
  const { isLoggedIn, isInitialised } = useAuthentication();

  const { getGeneralContext } = useDataContext();
  const context = getGeneralContext();
  const conditionalRoutes = useConditionalRoutes();
  const { goHome } = useNavigation(`usePageGuard on ${pathname}`);

  useEffect(() => {
    if (!isInitialised) {
      return;
    }

    // registration handles its own redirections
    if (pathname === Path.Registration || !conditionalRoutes) {
      return;
    }

    const condition = conditionalRoutes[pathname];

    // the page we are on is no longer available due to its condition
    const isPageGone = condition && !evaluateCondition(condition, context);

    if (isPageGone) {
      goHome();
    }
  }, [conditionalRoutes, isLoggedIn, context, pathname, goHome, isInitialised]);
}

export function useConditionalRoutes() {
  const {
    more: { settings },
  } = useConfig();

  return getRoutesFromSettings(settings) ?? {};
}

function getRoutesFromSettings(settings: MoreItem[]): RoutesConditions {
  return settings?.reduce((acc, item) => {
    const pathname = getSettingItemTargetUrl(item);

    return { ...acc, [pathname]: item.condition };
  }, {});
}

type RoutesConditions = { [pathname: string]: Condition };

/**
 * It's simplified version of the menu navigation target.
 * We don't care about external urls on purpose
 */
function getSettingItemTargetUrl(item: MoreItem): string {
  const { id: destinationId, type: destinationType } =
    item.action?.params?.destination ?? {};
  const settingUrl = '/settings/' + item.key;

  switch (destinationType) {
    case NavigationType.Page:
      return `/page/${destinationId}`;
    case NavigationType.Internal:
      return (
        destinationRoutes[destinationId] || `/${destinationId.toLowerCase()}`
      );

    default:
      return settingUrl;
  }
}
